import { HttpErrorResponse } from '@angular/common/http';

interface ErrorResponseBody {
  code?: string;
  detail?: string;
  message?: string;
  validationErrors?: Array<{ field?: string; message?: string }>;
}

/**
 * Parsea el body de un HttpErrorResponse (contrato ErrorResponse de
 * GlobalExceptionHandler.java), incluso si llega como string sin parsear.
 */
function parseErrorBody(error: unknown): ErrorResponseBody | null {
  if (!(error instanceof HttpErrorResponse)) {
    return null;
  }
  let body: unknown = error.error;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      return null;
    }
  }
  return body && typeof body === 'object' ? (body as ErrorResponseBody) : null;
}

/**
 * Extrae el mensaje de error legible desde el catch de una llamada HTTP.
 * Prioridad: `detail` (mensaje seguro específico, contrato ErrorResponse de
 * GlobalExceptionHandler.java) sobre `message` (categoría genérica); si hay
 * `validationErrors`, se concatenan al mensaje base.
 */
export function extractHttpErrorMessage(
  error: unknown,
  fallback: string
): string {
  if (error instanceof HttpErrorResponse) {
    const b = parseErrorBody(error);
    if (b) {
      const base = b.detail || b.message || fallback;
      const validationErrors = Array.isArray(b.validationErrors)
        ? b.validationErrors
        : [];
      if (validationErrors.length > 0) {
        const detalles = validationErrors
          .map((v) =>
            v.field ? `${v.field}: ${v.message ?? ''}` : (v.message ?? '')
          )
          .filter((s) => s.trim().length > 0)
          .join('\n');
        return detalles ? `${base}\n${detalles}` : base;
      }
      return base;
    }
    return fallback;
  }
  if (error instanceof Error) {
    return error.message || fallback;
  }
  return fallback;
}

/**
 * Devuelve el `detail` del body de error (mensaje seguro y específico del contrato
 * ErrorResponse), o `null` si no viene. A diferencia de extractHttpErrorMessage, no cae
 * en `message` (categoría genérica, p.ej. "Error de validación en los datos de entrada")
 * ni agrega los nombres técnicos de campo de `validationErrors`.
 */
export function extractHttpErrorDetail(error: unknown): string | null {
  const detail = parseErrorBody(error)?.detail;
  return typeof detail === 'string' && detail.trim().length > 0 ? detail : null;
}

/**
 * Mensaje de fallo técnico del legacy (WidgetsNamesConstants.genericError).
 */
export const MENSAJE_ERROR_TECNICO =
  'Estimado usuario, en estos momentos estamos presentando fallas técnicas. Por favor contacte al administrador.';

/**
 * Mensaje genérico para fallos de comunicación (HttpErrorResponse con status 0: red caída,
 * despliegue, corte del gateway, timeout). Debe coincidir con la clave
 * `notification.serviceCommunicationError` de es.json (el interceptor no puede resolver la
 * traducción de forma síncrona sin crear un ciclo HttpClient → TranslateLoader).
 */
export const MENSAJE_ERROR_COMUNICACION =
  'Tuvimos problemas realizando esta operación, por favor intenta de nuevo más tarde';

/**
 * Devuelve un HttpErrorResponse equivalente (mismo status, headers, url y body) cuyo
 * `message` es el texto indicado. El `message` original de Angular ("Http failure response
 * for <url>: 0 Unknown Error") es técnico: expone la URL del servicio y no debe llegar a
 * pantalla cuando un componente lo pinta con `err.message`.
 */
export function conMensajeGenerico(
  error: HttpErrorResponse,
  mensaje: string
): HttpErrorResponse {
  const generico = new HttpErrorResponse({
    error: error.error,
    headers: error.headers,
    status: error.status,
    statusText: error.statusText,
    url: error.url ?? undefined
  });
  Object.defineProperty(generico, 'message', {
    value: mensaje,
    enumerable: true,
    configurable: true,
    writable: true
  });
  return generico;
}

/**
 * Valor de `code` en el body de error cuando el backend rechaza un cumplimiento tardío sin
 * justificación (BusinessException.Type.CUMPLIMIENTO_JUSTIFICACION_REQUERIDA). OJO: el
 * GlobalExceptionHandler serializa `code` con el título de la excepción
 * ("cumplimiento.error.justificacion"), no con su código interno JUSTIFICACION_REQUERIDA.
 */
export const CODIGO_ERROR_JUSTIFICACION_REQUERIDA = 'cumplimiento.error.justificacion';

/**
 * Detecta el rechazo del backend por cumplimiento tardío sin justificación
 * (HTTP 400 con el código CODIGO_ERROR_JUSTIFICACION_REQUERIDA).
 */
export function esJustificacionRequerida(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse) || error.status !== 400) {
    return false;
  }
  return parseErrorBody(error)?.code === CODIGO_ERROR_JUSTIFICACION_REQUERIDA;
}

/**
 * Mensaje accionable para cuando el cdpersona del formulario ya no existe en
 * TPSC_PERSONAS (ver ContactoPersonaUseCase.validarPersonaExiste en backend).
 */
export const MENSAJE_PERSONA_DESACTUALIZADA =
  'Los datos de esta pantalla están desactualizados: la persona asociada ya no existe. Cierra este diálogo y vuelve a abrirlo para continuar.';

/**
 * Detecta el caso puntual de "cdpersona no existe" al crear/actualizar un
 * contacto. El código de negocio (ENTITY_NOT_FOUND) es genérico y se reutiliza
 * para otras entidades (ej. ContactoPersona, Centro de trabajo), así que se
 * distingue por el texto exacto que arma BusinessException.Type.ENTITY_NOT_FOUND
 * para la entidad "Persona" — no basta con buscar "Persona" como substring
 * porque "ContactoPersona" también la contiene.
 */
export function esPersonaNoEncontrada(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse) || error.status !== 404) {
    return false;
  }
  const b = parseErrorBody(error);
  const mensaje = b?.detail || b?.message || '';
  return mensaje.includes('entidad Persona con identificador');
}
